import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import type { TeamKnowledgeBaseEntry, KnowledgeBaseAttachment } from '@/types/team';

export const useTeamKnowledgeBase = (teamId?: string) => {
  const { user } = useAuth();
  const [entries, setEntries] = useState<TeamKnowledgeBaseEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState<string[]>([]);

  // Fetch all knowledge base entries
  const fetchEntries = useCallback(async () => {
    if (!teamId) {
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('team_knowledge_base')
        .select('*')
        .eq('team_id', teamId)
        .order('is_pinned', { ascending: false })
        .order('updated_at', { ascending: false });

      if (error) throw error;

      // Transform data to match our type
      const transformedData: TeamKnowledgeBaseEntry[] = (data || []).map(entry => ({
        ...entry,
        attachments: (entry.attachments as unknown as KnowledgeBaseAttachment[]) || []
      }));

      setEntries(transformedData);
      
      // Extract unique categories
      const uniqueCategories = [...new Set(
        transformedData
          .map(e => e.category)
          .filter((c): c is string => !!c)
      )];
      setCategories(uniqueCategories);
    } catch (error) {
      console.error('Error fetching knowledge base:', error);
      toast.error('Fehler beim Laden der Wissensbasis');
    } finally {
      setLoading(false);
    }
  }, [teamId]);

  // Create new entry
  const createEntry = async (entry: {
    title: string;
    content: string;
    category?: string;
    is_pinned?: boolean;
    attachments?: KnowledgeBaseAttachment[];
  }) => {
    if (!teamId || !user) return null;

    try {
      const insertData = {
        team_id: teamId,
        title: entry.title,
        content: entry.content,
        category: entry.category,
        is_pinned: entry.is_pinned || false,
        attachments: JSON.parse(JSON.stringify(entry.attachments || [])),
        created_by: user.id
      };
      
      const { data, error } = await supabase
        .from('team_knowledge_base')
        .insert(insertData)
        .select()
        .single();

      if (error) throw error;

      toast.success('Eintrag erstellt');
      await fetchEntries();
      return data;
    } catch (error) {
      console.error('Error creating entry:', error);
      toast.error('Fehler beim Erstellen des Eintrags');
      return null;
    }
  };

  // Update entry
  const updateEntry = async (
    entryId: string, 
    updates: Partial<Pick<TeamKnowledgeBaseEntry, 'title' | 'content' | 'category' | 'is_pinned' | 'attachments'>>
  ) => {
    if (!user) return false;

    try {
      const updateData: Record<string, unknown> = {
        updated_by: user.id,
        updated_at: new Date().toISOString()
      };
      
      if (updates.title !== undefined) updateData.title = updates.title;
      if (updates.content !== undefined) updateData.content = updates.content;
      if (updates.category !== undefined) updateData.category = updates.category;
      if (updates.is_pinned !== undefined) updateData.is_pinned = updates.is_pinned;
      if (updates.attachments !== undefined) updateData.attachments = updates.attachments as unknown as Record<string, unknown>[];

      const { error } = await supabase
        .from('team_knowledge_base')
        .update(updateData)
        .eq('id', entryId);

      if (error) throw error;

      toast.success('Eintrag aktualisiert');
      await fetchEntries();
      return true;
    } catch (error) {
      console.error('Error updating entry:', error);
      toast.error('Fehler beim Aktualisieren des Eintrags');
      return false;
    }
  };

  // Delete entry
  const deleteEntry = async (entryId: string) => {
    try {
      const { error } = await supabase
        .from('team_knowledge_base')
        .delete()
        .eq('id', entryId);

      if (error) throw error;

      toast.success('Eintrag gelöscht');
      await fetchEntries();
      return true;
    } catch (error) {
      console.error('Error deleting entry:', error);
      toast.error('Fehler beim Löschen des Eintrags');
      return false;
    }
  };

  // Toggle pin status
  const togglePin = async (entryId: string, currentPinned: boolean) => {
    return updateEntry(entryId, { is_pinned: !currentPinned });
  };

  // Search entries
  const searchEntries = useCallback((query: string) => {
    if (!query.trim()) return entries;
    
    const lowerQuery = query.toLowerCase();
    return entries.filter(entry => 
      entry.title.toLowerCase().includes(lowerQuery) ||
      entry.content.toLowerCase().includes(lowerQuery) ||
      entry.category?.toLowerCase().includes(lowerQuery)
    );
  }, [entries]);

  // Filter by category
  const filterByCategory = useCallback((category: string | null) => {
    if (!category) return entries;
    return entries.filter(entry => entry.category === category);
  }, [entries]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  return {
    entries,
    categories,
    loading,
    createEntry,
    updateEntry,
    deleteEntry,
    togglePin,
    searchEntries,
    filterByCategory,
    refresh: fetchEntries
  };
};
